import React, { useEffect, useState, useRef } from 'react';
import { IconSend, IconPlus, IconMicrophone, IconWaveSine } from '@tabler/icons-react';
import { VoiceConversationModal } from './VoiceConversationModal';
import { YeppyChatMode } from './EmptyChatSuggestions';
export interface FamilyMember {
  id: string;
  name: string;
  avatar: string;
  type: 'child' | 'caregiver';
  role?: string;
}
interface BottomBarProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  participants?: FamilyMember[];
  onAddParticipant?: (member: FamilyMember) => void;
  onRemoveParticipant?: (memberId: string) => void;
  selectedChild?: FamilyMember;
  onChildChange?: (child: FamilyMember) => void;
  children?: FamilyMember[];
  caregivers?: FamilyMember[];
  onVoiceMessage?: () => void;
  onVoiceCall?: () => void;
  chatMode?: YeppyChatMode;
  onModeChange?: (mode: YeppyChatMode) => void;
  onUseTemplate?: () => void;
  showModeTabs?: boolean;
}
// Mode-specific placeholder text
const modePlaceholders: Record<YeppyChatMode, string> = {
  ascolto: 'Racconta qualcosa sui bimbi...',
  informazioni: 'Cosa vuoi sapere?',
  attivita: 'Cosa devi organizzare?'
};
export function BottomBar({
  onSendMessage,
  disabled = false,
  onVoiceMessage,
  onVoiceCall,
  chatMode = 'attivita',
  onUseTemplate
}: BottomBarProps) {
  const [inputValue, setInputValue] = useState('');
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasText = inputValue.trim().length > 0;
  const placeholder = modePlaceholders[chatMode] || 'Scrivi un messaggio...';
  // Auto-focus the textarea when the component mounts
  useEffect(() => {
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !disabled) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  const handleVoiceMessage = () => {
    if (onVoiceMessage) {
      onVoiceMessage();
    }
  };
  const handleVoiceCall = () => {
    setIsVoiceModalOpen(true);
    if (onVoiceCall) {
      onVoiceCall();
    }
  };
  const handleVoiceTranscription = (text: string) => {
    if (text.trim()) {
      onSendMessage(text.trim());
    }
  };
  return <>
      <div className="sticky bottom-0 left-0 right-0 bg-yeppy-bg px-4 pb-4 pt-2 z-10" data-id="element-80">
        {/* Input Card - Claude style */}
        <form onSubmit={handleSubmit} className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl overflow-hidden" data-id="element-81">
          {/* Text input area */}
          <div className="px-4 pt-3.5 pb-2" data-id="element-82">
            <textarea ref={textareaRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled} rows={1} className="w-full bg-transparent font-poppins text-[15px] text-yeppy-text placeholder-yeppy-text placeholder-opacity-35 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed resize-none leading-relaxed" style={{
            minHeight: '24px',
            maxHeight: '120px'
          }} aria-label="Scrivi messaggio" onInput={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = '24px';
            target.style.height = Math.min(target.scrollHeight, 120) + 'px';
          }} data-id="element-83" />
          </div>

          {/* Action buttons row */}
          <div className="flex items-center justify-between px-3 pb-3" data-id="element-84">
            {/* Left: Plus button */}
            <button onClick={onUseTemplate} className="w-10 h-10 rounded-full bg-yeppy-bg flex items-center justify-center flex-shrink-0 transition-colors active:scale-95" data-id="element-85">
              <IconPlus className="w-5 h-5 text-yeppy-text" stroke={1.5} data-id="element-86" />
            </button>

            {/* Right: Action buttons */}
            <div className="flex-1 relative" data-id="element-87">
              <button onClick={() => setIsVoiceModalOpen(true)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-yeppy-bg flex items-center justify-center transition-colors active:scale-95" data-id="element-88">
                <IconMicrophone className="w-4 h-4 text-yeppy-text" stroke={1.5} data-id="element-89" />
              </button>
            </div>
            <button onClick={handleSubmit} disabled={disabled || !inputValue.trim()} className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100" style={{
            backgroundColor: inputValue.trim() ? '#1D2A36' : '#F5F5F5',
            color: inputValue.trim() ? '#FFFFFB' : '#1D2A36'
          }} data-id="element-90">
              <IconSend className="w-5 h-5" stroke={1.5} data-id="element-91" />
            </button>
          </div>
        </form>
      </div>

      {/* Voice Conversation Modal */}
      <VoiceConversationModal isOpen={isVoiceModalOpen} onClose={() => setIsVoiceModalOpen(false)} onTranscription={handleVoiceTranscription} data-id="element-92" />
    </>;
}